import { ColumnsType } from "antd/es/table";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/core/utils/format";
import { getGroupedCellRowSpan } from "src/core/utils/table";
import {
  ContentWithHoverTotals,
  PropertyToSum,
} from "src/modules/ContentWithHoverTotals/ContentWithHoverTotals";
import { ValueWithCurrency } from "src/modules/ValueWithCurrency/ValueWithCurrency";
import { RecapSectionDataItem } from "src/types/priceConfigurator";

export function useBomExplosionRecapTableColumns(
  dataItems: RecapSectionDataItem[],
  hoverTotalsContentKey: string
) {
  const { t } = useTranslation();

  const getTableColumns = useCallback(() => {
    const hoverPropertiesToSum: PropertyToSum[] = [
      {
        property: "dm",
        label: t("tableColumn.dm"),
      },
    ];

    const columns: ColumnsType<RecapSectionDataItem> = [
      {
        title: t("tableColumn.father"),
        key: "fatherCodeDescription_column",
        align: "center",
        width: 140,
        render: (item: RecapSectionDataItem, record, index) => {
          return (
            (item.rowType ?? null) !== null ?
              item.costTypeMapping :
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={["costTypeMapping", "fatherCodeDescription"]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.fatherCodeDescription}
            </ContentWithHoverTotals>
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
          ]),
        }),
      },
      {
        title: t("tableColumn.fatherCode"),
        key: "fatherCode_column",
        align: "center",
        width: 130,
        render: (item: RecapSectionDataItem, record, index) => {
          return (
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={[
                "costTypeMapping",
                "fatherCodeDescription",
                "fatherCode",
              ]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.fatherCode}
            </ContentWithHoverTotals>
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
            "fatherCode",
          ]),
        }),
      },
      {
        title: t("tableColumn.componentDescriptionShort"),
        key: "componentDescription_column",
        align: "center",
        width: 140,
        render: (item: RecapSectionDataItem, record, index) => {
          return (
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={[
                "costTypeMapping",
                "fatherCodeDescription",
                "fatherCode",
                "componentDescription",
              ]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.componentDescription}
            </ContentWithHoverTotals>
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
            "fatherCode",
            "componentDescription",
          ]),
        }),
      },
      {
        title: t("tableColumn.component"),
        key: "component_column",
        width: 120,
        align: "center",
        render: (item: RecapSectionDataItem, record, index) => {
          return (
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={[
                "costTypeMapping",
                "fatherCodeDescription",
                "fatherCode",
                "componentDescription",
                "component",
              ]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.component}
            </ContentWithHoverTotals>
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
            "fatherCode",
            "componentDescription",
            "component",
          ]),
        }),
      },
      {
        title: t("tableColumn.from"),
        key: "from_column",
        align: "center",
        width: 150,
        render: (item: RecapSectionDataItem, record, index) => {
          return item.from;
        },
      },
      {
        title: t("tableColumn.dm"),
        key: "dm_column",
        align: "center",
        className: "bold",
        width: 80,
        render: (item: RecapSectionDataItem, record, index) => {
          return (
            <ValueWithCurrency
              value={item.dm}
              currencyCode={item.currencyCode}
            />
          );
        },
      },
      {
        title: t("tableColumn.pureQuantity"),
        key: "pure_quantity_column",
        align: "center",
        width: 80,
        render: (item: RecapSectionDataItem, record, index) => {
          return formatNumber(item.pureQuantity);
        },
      },
      {
        title: t("tableColumn.standardUnitCost"),
        key: "standardUnitCost_column",
        align: "center",
        width: 80,
        render: (item: RecapSectionDataItem, record, index) => {
          return (
            <ValueWithCurrency
              value={item.standardUnitCost}
              currencyCode={item.currencyCode}
            />
          );
        },
      },
      {
        title: t("tableColumn.unitOfMeasure"),
        key: "unitOfMeasure_column",
        align: "center",
        width: 80,
        render: (item: RecapSectionDataItem, record, index) => {
          return item.unitOfMeasure;
        },
      },
      {
        title: t("tableColumn.fopDescription"),
        key: "fopDescription_column",
        align: "center",
        width: 250,
        render: (item: RecapSectionDataItem, record, index) => {
          return item.fopDescription;
        },
      },
    ];

    return columns;
  }, [dataItems, hoverTotalsContentKey, t]);

  return {
    getTableColumns: getTableColumns,
  };
}
